import {
  AnalyticsFrequency,
  ExperimentsMetricType,
  ExperimentsSplitsMeasurementMetricDefinition,
  GraphType,
  IntervalUnit,
  NormalizationType,
  PredefinedAnalyticsMetricDefinition,
  UserDefinedAnalyticsMetricDefinition,
  UserDefinedMetricConfig,
  VisualQueryFilter,
  isEventMetricConfig,
  isModelMetricConfig,
  isPredefinedAnalyticsMetricDefinition,
  isRelatedColumn,
  isTraitColumn,
  isTransformedColumn,
  isUserDefinedAnalyticsMetricDefinition,
  transformSavedChartFrequency,
} from "@hightouch/lib/query/visual/types";
import {
  GroupByColumn,
  isGroupByColumnPlaceholder,
} from "@hightouch/lib/query/visual/types/analytics";
import { isValid } from "date-fns";
import { format } from "date-fns-tz";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import isEqualWith from "lodash/isEqualWith";
import { captureException } from "src/lib/monitoring";
import { isPresent } from "ts-extras";

import { GroupColumn } from "src/components/graphs/cross-audience-graph/types";
import { getSeriesDescription } from "src/components/graphs/cross-audience-graph/utils";
import { toSingleCondition } from "src/components/audiences/utils";
import {
  AnalyticsCohortDefinitionOutput,
  FilterableColumnFragment,
  FunnelMetricDataForCohort,
  FunnelMetricDataForCohortOrError,
} from "src/graphql";
import {
  AggregationOption,
  DEFAULT_ATTRIBUTION_WINDOW,
} from "src/pages/metrics/constants";
import {
  getAggregationConfiguration,
  mapAggregationConfigurationToConfigurationOption,
} from "src/pages/metrics/utils";
import {
  AnalyticsMetricDefinition,
  AndOrCondition,
  AttributionWindow,
  AudienceAggregationType,
  ColumnReference,
  EventCondition,
  PerUserAggregationType,
  PredefinedMetric,
  NestableCondition,
  Relationship,
  initialEventCondition,
  initialFunnelCondition,
  isSyntheticColumn,
} from "src/types/visual";

import { getModelIdFromColumn } from "src/components/explore/visual/utils";
import { DEFAULT_FILTER, PREDEFINED_METRIC_OPTIONS } from "./constants";
import { MetricResultMaybeFromCache } from "./hooks/use-metric-series";
import {
  PLACEHOLDER_AUDIENCE,
  SECONDS_IN_ONE_DAY,
  STATIC_ARRAY,
} from "./state/constants";
import {
  ChartTab,
  CombinedChartType,
  ConversionWindow,
  EventOrMetricOption,
  FunnelStep,
  FunnelStepGraphData,
  FunnelStepGraphDataPoint,
  FunnelTableData,
  MeasurementScope,
  MeasuringSelection,
  Metric,
  MetricSelection,
  ParentModel,
  SavedChartFunnelStage,
  SelectedAudience,
  TimeOptions,
  isAdHocAudience,
  isSavedMetric,
  isSyntheticMetric,
} from "./types";

export const numberAndStringValidator = (valueA, valueB) => {
  if (
    (typeof valueA === "string" || typeof valueA === "number") &&
    (typeof valueB === "string" || typeof valueB === "number")
  ) {
    return valueA == valueB;
  }

  return undefined;
};

/*
  We represent date ranges as the time between two dates,
  or the time since a single date.
*/
export function isValidDateRange(
  dateRange: Date[],
): dateRange is [Date] | [Date, Date] {
  return (
    (dateRange.length === 1 && isValid(dateRange[0])) ||
    (dateRange.length === 2 && isValid(dateRange[0]) && isValid(dateRange[1]))
  );
}

export function getNumberOfSeconds(
  window: ConversionWindow | undefined,
): number {
  if (!window) return 0;
  const { quantity, unit } = window;

  switch (unit) {
    case IntervalUnit.Day:
      return 60 * 60 * 24 * quantity;
    case IntervalUnit.Week:
      return 60 * 60 * 24 * 7 * quantity;
    case IntervalUnit.Month:
      return 60 * 60 * 24 * 30 * quantity;
    default:
      return 0;
  }
}

export const formatDatePickerLabel = (
  dateRange: Date[],
  timeValue: TimeOptions,
): string => {
  if (timeValue != TimeOptions.Custom || !isValidDateRange(dateRange)) {
    return "Custom";
  }

  if (dateRange.length === 1)
    return `${dateRange[0].toLocaleDateString()} - Today`;

  return `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`;
};

// Generates a time window between two dates, or a single date and the current date.
export const getInclusiveTimeWindow = (dateRange: [Date, Date] | [Date]) => {
  // Extract the year, month, and day from the date objects
  // while intentionally ignoring the browser's local timezone,
  // so we don't add any kind of offset to the dates.
  const start = format(dateRange[0], "yyyy-MM-dd'T'00:00:00.000");
  const endDate = dateRange[1] ?? new Date();
  const end = format(endDate, "yyyy-MM-dd'T'23:59:59.999");

  return { start, end };
};

export const isInitialMetric = (
  metric: Metric | undefined,
  {
    conditions,
    attributionWindow,
    normalization,
  }: {
    conditions: AndOrCondition<NestableCondition>[];
    attributionWindow?: AttributionWindow;
    normalization?: NormalizationType;
  },
) => {
  if (!metric) {
    return false;
  }

  return (
    isEqual(
      conditions,
      toSingleCondition(metric?.config.filter?.subconditions),
    ) &&
    isEqual(attributionWindow, metric.attribution_window) &&
    normalization === metric.normalization
  );
};

export const formatMetricsAndMetricDefinitions = (
  parentModelId: string | undefined,
  metricSelections: MetricSelection[],
  metrics: Metric[],
): { metricIds: string[]; metricDefinitions: AnalyticsMetricDefinition[] } => {
  const metricIds: string[] = [];
  const metricDefinitions: AnalyticsMetricDefinition[] = [];

  if (!parentModelId) {
    // Bail if there is no parent model ID since it's needed for to make queries
    return {
      metricIds,
      metricDefinitions,
    };
  }

  metricSelections.forEach((metricSelection) => {
    if (!metricSelection.id) {
      return;
    }

    const initialMetric = getMetricById(metrics, metricSelection.id);

    if (isInitialMetric(initialMetric, metricSelection)) {
      // The selected metric is already persisted, so we can reference it by the ID
      metricIds.push(metricSelection.id);
    } else if (
      Object.values(PredefinedMetric).includes(
        metricSelection.id as PredefinedMetric,
      )
    ) {
      // The selected metric is "pre-defined" -- a special metric that is not created by the user,
      // but is understood by the backend. It has its own definition.
      const metric: PredefinedAnalyticsMetricDefinition = {
        parentModelId: parentModelId.toString(),
        predefinedMetric: metricSelection.id,
      };
      metricDefinitions.push(metric);
    } else {
      // The selected metric is ad-hoc, so we need to build out a valid definition for it
      let eventModelId = metricSelection?.eventModelId?.toString() || undefined;
      if (initialMetric && isEventMetricConfig(initialMetric.config)) {
        eventModelId = initialMetric.config.eventModelId;
      }
      const modelId = metricSelection.modelId || undefined;

      // TODO (INS-375): this errors in getRelationshipPath if we set the eventModelId
      // or modelId as the relationshipId, because segment ids are UUIDs, but the
      // relationshipId is a bigint.
      const relationshipId =
        eventModelId || modelId
          ? (initialMetric?.config.relationshipId?.toString() ??
            metricSelection.id.toString())
          : null;
      const column =
        initialMetric && "column" in initialMetric.config
          ? initialMetric?.config.column
          : (metricSelection.column?.column_reference ?? null);

      const aggregationConfiguration = getAggregationConfiguration(
        metricSelection?.aggregationMethod ?? AggregationOption.Count,
      );

      let config: UserDefinedMetricConfig;

      if ((!eventModelId && !relationshipId) || (!modelId && !relationshipId)) {
        // Return early if the metric selection is invalid.
        // This can happen when the parentModelId is set, but
        // we are still waiting on `events` or `metrics` to load.
        return;
      }

      const commonConfig: Pick<UserDefinedMetricConfig, "column" | "filter"> = {
        column: column ?? metricSelection.column?.column_reference,
        filter: {
          subconditions: metricSelection.conditions,
        },
      };

      if (eventModelId && relationshipId) {
        config = {
          eventModelId,
          relationshipId,
          ...commonConfig,
        };
      } else if (modelId && relationshipId) {
        config = {
          modelId,
          relationshipId,
          ...commonConfig,
        };
      } else if (modelId) {
        config = {
          modelId,
          ...commonConfig,
        };
      } else {
        // We should never reach this point unless the metric selection is invalid.
        throw new Error("Invalid metric selection");
      }

      const metric: UserDefinedAnalyticsMetricDefinition = {
        id: metricSelection.id,
        parentModelId: parentModelId?.toString() ?? "",
        ...(aggregationConfiguration ?? {
          aggregation: PerUserAggregationType.Count,
          audienceAggregation: AudienceAggregationType.Sum,
        }),
        attributionWindow:
          metricSelection.attributionWindow ?? DEFAULT_ATTRIBUTION_WINDOW,
        config,
        cacheKey: metricSelection.cacheKey,
      };

      metricDefinitions.push(metric);
    }
  });

  return { metricIds, metricDefinitions };
};

const formatMetricDefinitionForSplitsMeasurement = (
  metric:
    | UserDefinedAnalyticsMetricDefinition
    | PredefinedAnalyticsMetricDefinition,
  globalMeasurementWindow: AttributionWindow,
  globalNormalization?: NormalizationType,
):
  | ExperimentsSplitsMeasurementMetricDefinition
  | PredefinedAnalyticsMetricDefinition => {
  if (isPredefinedAnalyticsMetricDefinition(metric)) return metric;

  const { config, ...metricData } = metric;

  return {
    config: {
      ...config,
      normalization: globalNormalization,
    },
    ...metricData,
    metricType: ExperimentsMetricType.SplitsMeasurement,
    attributionWindow: globalMeasurementWindow,
  };
};

// TODO(davidlumley): How do we want to handle formatting saved metric ids?
export const formatMetricSelectionForSplitsMeasurement = (
  parentModelId: string | undefined,
  metricSelections: MetricSelection[],
  metrics: Metric[],
  globalMeasurementWindow: AttributionWindow,
  globalNormalization?: NormalizationType,
): { metricIds: string[]; metricDefinitions: AnalyticsMetricDefinition[] } => {
  const { metricIds, metricDefinitions } = formatMetricsAndMetricDefinitions(
    parentModelId,
    metricSelections,
    metrics,
  );

  const reconstructedSavedMetrics: AnalyticsMetricDefinition[] = metricIds
    .map((metricId) => {
      const metric = metrics.find(({ id }) => id === metricId);
      if (!metric) {
        return null;
      }
      // Reconstruct the metric definition from the metric id
      // so that we can apply the normalization and measurement window
      return {
        parentModelId: parentModelId?.toString() ?? "",
        id: metricId,
        name: metric.name,
        description: metric.description,
        aggregation: metric.aggregation,
        audienceAggregation: metric.audienceAggregation,
        attributionWindow: metric.attribution_window,
        config: metric.config,
      };
    })
    .filter(isPresent);

  return {
    metricIds: STATIC_ARRAY,
    metricDefinitions: [...metricDefinitions, ...reconstructedSavedMetrics]
      .filter(
        (metric) =>
          isUserDefinedAnalyticsMetricDefinition(metric) ||
          isPredefinedAnalyticsMetricDefinition(metric),
      )
      .map((metric) =>
        formatMetricDefinitionForSplitsMeasurement(
          metric,
          globalMeasurementWindow,
          globalNormalization,
        ),
      ),
  };
};

export const mapMetricToMetricSelection = (
  goalDefinition: Metric,
  metrics: Metric[],
  parentColumns: FilterableColumnFragment[],
  events: Relationship[],
): MetricSelection | null => {
  if (!goalDefinition.config.relationshipId) return null;

  // need to get the metric or event name and id
  const event = events?.find(({ id }) => id.toString() === goalDefinition.id);
  const metric = metrics.find(({ id }) => id.toString() === goalDefinition.id);
  const predefinedMetric = PREDEFINED_METRIC_OPTIONS.find(
    ({ id }) => id === goalDefinition.id,
  );

  const aggregationMethod =
    mapAggregationConfigurationToConfigurationOption(goalDefinition) ??
    AggregationOption.Count;

  let name = "";
  let description: string | null = null;

  if (metric) {
    name = metric.name;
    description = metric.description;
  } else if (event) {
    name = event.to_model.name ?? event.name;
    description = event.to_model.description;
  } else if (predefinedMetric) {
    name = predefinedMetric.name;
  }

  if (!isEventMetricConfig(goalDefinition.config)) {
    throw new Error("MetricConfig is not an EventMetricConfig");
  }

  return {
    // These need to be NUMBERS but the type says string >.<
    id: Number(goalDefinition.config.relationshipId) as unknown as string,
    eventModelId: goalDefinition.config.eventModelId
      ? (Number(goalDefinition.config.eventModelId) as unknown as string)
      : null,
    modelId: isModelMetricConfig(goalDefinition.config)
      ? (Number(goalDefinition.config.modelId) as unknown as string)
      : null,
    name,
    description,
    aggregationMethod,
    conditions: goalDefinition.config.filter.subconditions ?? [],
    attributionWindow: goalDefinition.attribution_window,
    column: goalDefinition.config.column
      ? parentColumns
          .concat(event?.to_model.filterable_audience_columns ?? [])
          .find(({ column_reference }) => {
            return isEqualWith(
              column_reference,
              goalDefinition.config.column,
              numberAndStringValidator,
            );
          })
      : undefined,
  };
};

export const separateMetricsAndMetricDefinitions = (
  parentModelId: string | undefined,
  metricSelections: MetricSelection[],
  metrics: Metric[],
): { metricIds: string[]; metricDefinitions: MetricSelection[] } => {
  const metricIds: string[] = [];
  const metricDefinitions: MetricSelection[] = [];

  if (!parentModelId) {
    // Bail if there is no parent model ID since it's needed for to make queries
    return {
      metricIds,
      metricDefinitions,
    };
  }

  metricSelections.forEach((metricSelection) => {
    if (!metricSelection.id) {
      return;
    }

    const initialMetric = getMetricById(metrics, metricSelection.id);

    if (isInitialMetric(initialMetric, metricSelection)) {
      // The selected metric is already persisted, so we can reference it by the ID
      metricIds.push(metricSelection.id);
    } else {
      metricDefinitions.push(metricSelection);
    }
  });

  return { metricIds, metricDefinitions };
};

export const separateCohortsAndCohortDefinitions = (
  parentModelId: number | string | undefined | null,
  audiences: SelectedAudience[],
) => {
  const cohortIds: string[] = [];
  const cohortDefinitions: {
    parentModelId: string;
    filter: VisualQueryFilter;
    name: string;
    subsetIds: string[];
  }[] = [];

  for (const audience of audiences) {
    if (audience.id == parentModelId) {
      const filter = isAdHocAudience(audience)
        ? audience.filter
        : DEFAULT_FILTER;

      cohortDefinitions.push({
        parentModelId: audience.id.toString(),
        filter,
        name: audience.name,
        subsetIds: audience.subsetIds ?? [],
      });
    } else if (audience.id !== PLACEHOLDER_AUDIENCE.id) {
      cohortIds.push(audience.id.toString());
    }
  }

  return { cohortIds, cohortDefinitions };
};

export const getMetricById = (
  metrics: Metric[],
  metricId: string,
): Metric | undefined => {
  return metrics.find(({ id }) => id === metricId);
};

export const getEventById = (
  events: Relationship[],
  {
    eventModelId,
    relationshipId,
  }: { eventModelId?: string | number; relationshipId?: string | number },
): Relationship | undefined => {
  return events.find(
    ({ id, to_model }) =>
      id.toString() === relationshipId?.toString() ||
      to_model.id.toString() === eventModelId?.toString(),
  );
};

export const getEventIdFromMetricSelection = (
  metric: Pick<MetricSelection, "eventModelId" | "id">,
  metrics: Metric[],
) => {
  if (metric.eventModelId) {
    return metric.eventModelId;
  }

  if (metric.id) {
    const metricDefinition = getMetricById(metrics, metric.id);
    if (metricDefinition && isEventMetricConfig(metricDefinition.config)) {
      return metricDefinition.config.eventModelId;
    }
  }

  return null;
};

export const getMetricSelectionFromMetricOption = ({
  metricOption,
  events,
  metrics,
}: {
  metricOption: EventOrMetricOption;
  events: Relationship[];
  metrics: Metric[];
}): MetricSelection | undefined => {
  if (isSyntheticMetric(metricOption)) {
    return {
      id: metricOption.id,
      eventModelId: null,
      modelId: null,
      name: metricOption.name,
      description: null,
      aggregationMethod: AggregationOption.Count,
      attributionWindow: undefined,
      conditions: [],
      source: metricOption.source,
      resourceId: metricOption.resourceId,
    };
  } else if (metricOption.relationshipId) {
    // Event Model Metrics, Related Model Metrics
    return {
      id: metricOption.id,
      traitId: metricOption.traitId,
      eventModelId: metricOption.eventModelId,
      relationshipId: metricOption.relationshipId,
      modelId: metricOption.modelId,
      name: metricOption.name,
      description: null,
      aggregationMethod: AggregationOption.Count,
      attributionWindow: undefined,
      conditions: [],
      campaignIdColumn: metricOption.campaignIdColumn,
      eventType: metricOption.eventType,
    };
  } else if (metricOption.modelId) {
    // Parent Model Metric
    return {
      id: metricOption.id,
      traitId: metricOption.traitId,
      eventModelId: metricOption.eventModelId,
      relationshipId: metricOption.relationshipId,
      modelId: metricOption.modelId,
      name: metricOption.name,
      description: null,
      aggregationMethod: AggregationOption.Count,
      attributionWindow: undefined,
      conditions: [],
    };
  }

  return getMetricSelectionFromMetric({
    metricId: metricOption.id,
    events,
    metrics,
  });
};

export const getMetricSelectionFromMetric = ({
  metricId,
  events,
  metrics,
}: {
  metricId: string;
  events: Relationship[];
  metrics: Metric[];
}): MetricSelection | undefined => {
  const event = events?.find(({ id }) => id === metricId);
  const metric = metrics.find(({ id }) => id === metricId);
  const predefinedMetric = PREDEFINED_METRIC_OPTIONS.find(
    ({ id }) => id === metricId,
  );

  let data: MetricSelection | undefined;

  if (metric) {
    data = {
      id: metric.id,
      eventModelId: null,
      modelId: null,
      name: metric.name,
      description: metric.description,
      aggregationMethod:
        mapAggregationConfigurationToConfigurationOption(metric) ??
        AggregationOption.Count,
      attributionWindow: metric?.attribution_window,
      conditions:
        (metric?.config.filter?.subconditions as NestableCondition[]) ?? [],
    };
  } else if (event) {
    data = {
      id: event.id,
      eventModelId: event.to_model.id,
      modelId: null,
      name: event.to_model.name ?? event.name,
      description: event.to_model.description,
      aggregationMethod: AggregationOption.Count,
      attributionWindow: undefined,
      conditions: [],
    };
  } else if (predefinedMetric) {
    data = {
      id: predefinedMetric.id,
      eventModelId: null,
      modelId: null,
      name: predefinedMetric.name,
      description: null,
      // `aggregationMethod` is ignored for predefined metrics because
      // it has a specific aggregation that can't be modified by the user.
      // This is just a placeholder to satisfy typechecking.
      // TODO(samuel): think of a better way to do this. Maybe by creating an AggregationOption.Unknown.
      aggregationMethod: AggregationOption.Count,
      attributionWindow: undefined,
      conditions: [],
    };
  }

  return data;
};

export const getNumberOfUniqueValues = <T>(
  values: Array<T>,
  path?: string | null,
  omit?: (string | boolean | number | undefined | null)[],
): number => {
  let result = 0;
  const map = new Map<T, number>();

  values.forEach((dataPoint) => {
    const value = path ? get(dataPoint, path) : dataPoint;

    if (omit?.includes(value)) {
      return;
    }

    const count = map.get(value);

    map.set(path ? `${path}:${value}` : value, count ?? 1);
  });

  // biome-ignore lint/suspicious/noAssignInExpressions: intentional
  map.forEach((value) => (result += value));

  return result;
};

export const getUnsupportedMetricNamesForGroupByColumn = ({
  events,
  groupByColumn,
  metrics,
  metricSelection,
  parent,
  relatedModels,
}: {
  parent: ParentModel | null;
  groupByColumn: GroupByColumn | undefined;
  metricSelection: MetricSelection[];
  metrics: Metric[];
  events: Relationship[];
  relatedModels: Relationship[];
}): string[] => {
  // step 1: get events that are selected that aren't applicable
  const unsupportedMetricNames: string[] = [];

  if (!groupByColumn) return [];
  if (isGroupByColumnPlaceholder(groupByColumn)) return [];
  // If a synthetic column, can exit early since these columns exist in a separate
  // model and only apply to specific measurement options (e.g. decision engine flow)
  if (isSyntheticColumn(groupByColumn)) return [];

  const isRelatedToParent = isGroupByColumnRelatedToParent(
    parent,
    groupByColumn,
  );
  // If the group by column is related to the parent model, we can exit early
  if (isRelatedToParent) return [];

  const relatedModel = [...events, ...relatedModels].find((relationship) =>
    isGroupByColumnRelatedToModel(relationship, groupByColumn),
  );
  const groupByModelId =
    relatedModel?.to_model?.id || getModelIdFromColumn(groupByColumn);
  const parentId = parent?.id.toString();

  // For each metric, check if it's supported by the groupBy column
  for (const metric of metricSelection) {
    if (isSavedMetric(metric)) {
      const metricDefinition = getMetricById(metrics, metric.id);

      if (metricDefinition && isEventMetricConfig(metricDefinition.config)) {
        const eventModelId = metricDefinition?.config?.eventModelId?.toString();
        if (groupByModelId != eventModelId) {
          unsupportedMetricNames.push(`"${metricDefinition.name}"`);
        }
      }
      continue;
    }

    const isMismatchedEventMetric = Boolean(
      metric.id &&
        metric.eventModelId &&
        groupByModelId != metric.eventModelId?.toString(),
    );
    if (isMismatchedEventMetric) {
      if (!metric.eventModelId) {
        // NOTE: This error indicates a bug in `isMismatchedEventMetric`
        captureException(
          new Error(
            "isMismatchedEventMetric is true, but metric has no eventModelId",
          ),
          { extra: { metric } },
        );
        continue;
      }

      const event = getEventById(events, {
        eventModelId: metric.eventModelId,
        relationshipId: metric.id,
      });

      if (event?.to_model.name) {
        unsupportedMetricNames.push(`"${event.to_model.name}"`);
      }
      continue;
    }

    const isMismatchedModelMetric = Boolean(
      metric.id &&
        metric.modelId &&
        groupByModelId != metric.modelId?.toString(),
    );
    if (isMismatchedModelMetric) {
      if (metric.modelId === parentId) {
        unsupportedMetricNames.push(`"${parent?.name}"`);
      }

      const relatedModel = relatedModels.find(
        (model) => model.to_model.id.toString() === metric.modelId,
      );
      if (relatedModel)
        unsupportedMetricNames.push(`"${relatedModel.to_model.name}"`);
      continue;
    }
  }

  return unsupportedMetricNames;
};

export const EMPTY_AUDIENCE_NAME = "--";

export const getCombinedChartType = ({
  chartTab,
  graphType,
}: {
  chartTab: ChartTab;
  graphType: GraphType | undefined;
}): CombinedChartType | undefined =>
  chartTab === ChartTab.Insights ? graphType : chartTab;

export const getSavedChartData = ({
  cohorts,
  customDateRange,
  funnelSteps,
  metrics,
  metricSelection,
  parentModelId,
  timeValue,
  measuringSelection,
  chartTab,
  graphType,
  rollupFrequency,
}: {
  cohorts: SelectedAudience[];
  customDateRange: Date[];
  funnelSteps: FunnelStep[];
  metrics: Metric[];
  metricSelection: MetricSelection[];
  parentModelId: number | undefined | null;
  timeValue: TimeOptions;
  measuringSelection: MeasuringSelection | undefined;
  chartTab: ChartTab;
  graphType: GraphType | undefined;
  rollupFrequency: AnalyticsFrequency | undefined;
}) => {
  const { metricIds, metricDefinitions } = separateMetricsAndMetricDefinitions(
    parentModelId?.toString() ?? "",
    metricSelection,
    metrics,
  );

  const formattedMetricIds = metricIds.map((id) => ({ goal_id: id }));
  const formattedMetricDefinitions = metricDefinitions.map((value) => ({
    goal_definition: value,
  }));
  const metricData: (
    | { goal_id: string }
    | { goal_definition: MetricSelection }
  )[] = [...formattedMetricIds, ...formattedMetricDefinitions];
  const { cohortIds, cohortDefinitions } = separateCohortsAndCohortDefinitions(
    parentModelId,
    cohorts,
  );

  const formattedCohortIds = cohortIds.map((id) => ({ cohort_id: id }));
  const formattedCohortDefinitions = cohortDefinitions.map((value) => ({
    cohort_definition: value,
  }));
  const cohortData: { cohort_id?: string; cohort_definition?: object }[] = [
    ...formattedCohortIds,
    ...formattedCohortDefinitions,
  ];

  const funnelStageData: SavedChartFunnelStage[] = funnelSteps
    .map(({ eventModelId, relationshipId, subconditions }) => {
      if (!eventModelId || !relationshipId) return null;

      const formattedRow = {
        event_model_id: eventModelId,
        relationship_id: relationshipId,
      };
      if (subconditions.length > 0) {
        return { ...formattedRow, funnel_stage_definition: { subconditions } };
      }

      return formattedRow;
    })
    .filter(isPresent);

  // lookbackWindow is derived from `timeValue` and `customDateRange`
  let lookbackWindow: { start: string; end?: string } | { lookback: string };

  if (timeValue === TimeOptions.Custom && isValidDateRange(customDateRange)) {
    lookbackWindow = {
      start: customDateRange[0]?.toISOString(),
      end: customDateRange[1]?.toISOString(),
    };
  } else {
    lookbackWindow = { lookback: timeValue };
  }

  return {
    metrics: metricData,
    cohorts: cohortData,
    funnelSteps: funnelStageData,
    lookbackWindow,
    // XXX: we use "" id for measuring all data for group options type requirements
    measuringId: measuringSelection?.id || null,
    chartType: getCombinedChartType({ graphType, chartTab }),
    frequency: transformSavedChartFrequency(rollupFrequency, graphType),
  };
};

export const transformFunnelDataForGraph = ({
  audiences,
  data = [],
  events,
}: {
  audiences: SelectedAudience[];
  data: FunnelMetricDataForCohort[];
  events: Relationship[];
}): FunnelStepGraphData[] => {
  // Add all steps into funnel data
  const funnelData: FunnelStepGraphData[] =
    data[0]?.groups?.[0]?.stages?.map(({ stage }, index) => {
      const event = getEventById(events ?? [], stage);

      return {
        stageName: event?.to_model.name ?? `Event ${stage.relationshipId}`,
        eventModelId: event?.to_model.id ?? "",
        relationshipId: event?.id ?? "",
        subconditions: stage.filter?.conditions ?? null,
        groupByValue: undefined,
        index,
        data: {},
      };
    }) ?? [];

  // Format data for funnel graph
  data.forEach((cohortPairing) => {
    const cohort = cohortPairing?.cohort;
    const audienceId = Number(
      cohort?.cohortDefinition?.parentModelId ?? cohort?.cohortId,
    );

    const foundAudience = findAudienceFromCohort(
      audiences,
      cohort?.cohortDefinition?.filter,
      audienceId,
    );

    const audienceName = foundAudience?.name ?? EMPTY_AUDIENCE_NAME;

    (cohortPairing?.groups ?? []).forEach(({ groupBy, splitId, stages }) => {
      // XXX: Need to replace all brackets bc recharts dataKey's won't work with
      // them since the key is used as dot notation to access the data. This is
      // a quick work around (longer term solution would need to update the dataKey
      // to be a function that grabs the data instead and update our hover state
      // tooltip to account for that)
      const seriesDescription = getSeriesDescription({
        audienceName,
        splitName: splitId,
        groupByColumns: groupBy,
      })
        .replaceAll("[", "(")
        .replaceAll("]", ")");

      const groupByValue = groupBy?.[0]?.value ?? "--";

      // stage is the selected step
      stages.forEach(({ count }, index) => {
        let rowData: FunnelStepGraphDataPoint = {
          count,
          conversion: 0,
          conversionBarSize: 0,
          dropOffBarSize: 0,
          groupByValue,
        };

        if (index === 0) {
          // First step
          rowData.conversion = count !== 0 ? 1 : 0;
          rowData.conversionBarSize = count !== 0 ? 1 : 0;
        } else {
          const initialItem = stages[0]!;
          const previousItem = stages[index - 1]!;
          const previousRow = funnelData[index - 1]!.data[seriesDescription]!;

          const initialCount = initialItem.count;
          const previousCount = previousItem.count;

          const conversionBarSize =
            initialCount === 0 ? 0 : count / initialCount;

          rowData = {
            count,
            groupByValue,
            conversion: previousCount === 0 ? 0 : count / previousCount, // conversion from previous to current step
            conversionBarSize, // current count / initial count
            dropOffBarSize: previousRow.conversionBarSize - conversionBarSize,
          };
        }

        funnelData[index]!.data[seriesDescription] = rowData;
      });
    });
  });

  return funnelData;
};

export function filterMetricDataForCohort(
  metricData: FunnelMetricDataForCohortOrError[],
): FunnelMetricDataForCohort[] {
  const result: FunnelMetricDataForCohort[] = [];

  metricData.forEach((data) => {
    if ("groups" in data) {
      result.push(data);
    }
  });

  return result;
}

export const getTableDataFromFunnelData = (
  graphData: FunnelStepGraphData[],
): FunnelTableData[] => {
  if (graphData.length === 0) return [];

  // 1. Extract series names
  const seriesNames = Object.keys(graphData[0]!.data);

  // 2. Organize the data
  return seriesNames.map((name) => {
    const row: FunnelTableData = {
      seriesName: name,
      conversion: graphData[graphData.length - 1]!.data[name]!.conversion,
      steps: graphData.map(({ stageName, data }) => ({
        stageName,
        numberOfUsers: data[name]!.count,
        conversion: data[name]!.conversion,
      })),
    };

    return row;
  });
};

export const removeDisabledSplitGroupsFromMetricResult = (
  data: MetricResultMaybeFromCache[],
  audiences: {
    id: string | number;
    name: string;
    splits: { enabled: boolean; name: string }[];
  }[],
) => {
  const disabledSplitMap: Record<string, Set<string>> = audiences.reduce(
    (all, { id, splits = [] }) => {
      const disabledSplitNames = new Set(
        splits.filter(({ enabled }) => !enabled).map(({ name }) => name),
      );
      all[id.toString()] = disabledSplitNames;
      return all;
    },
    {},
  );

  return data.map((series) => {
    if ("data" in series.result) {
      const disabledSeriesNames =
        series.ids.cohortId && disabledSplitMap[series.ids.cohortId.toString()];

      if (disabledSeriesNames) {
        return {
          ...series,
          result: {
            ...series.result,
            data: series.result.data.filter(
              ({ splitId }) =>
                splitId === null || !disabledSeriesNames.has(splitId),
            ),
          },
        };
      }
    }

    return series;
  });
};

export const removeDisabledSplitGroupsFromFunnels = (
  data: FunnelMetricDataForCohort[],
  audiences: {
    id: string | number;
    name: string;
    splits: { enabled: boolean; name: string }[];
  }[],
): FunnelMetricDataForCohort[] => {
  const disabledSplitMap: Record<string, Set<string>> = audiences.reduce(
    (all, { id, splits = [] }) => {
      const disabledSplitNames = new Set(
        splits.filter(({ enabled }) => !enabled).map(({ name }) => name),
      );
      all[id.toString()] = disabledSplitNames;
      return all;
    },
    {},
  );

  return data.map((series) => {
    const disabledSeriesNames =
      series.cohort.cohortId &&
      disabledSplitMap[series.cohort.cohortId.toString()];

    if (disabledSeriesNames) {
      return {
        ...series,
        groups: series.groups.filter(
          ({ splitId }) =>
            splitId === null || !disabledSeriesNames.has(splitId),
        ),
      };
    }

    return series;
  });
};

// TODO(samuel): update once event conditions support more than two events
export const createEventConditionFromFunnelSteps = ({
  stage,
  secondStage,
  didPerform = true,
}: {
  stage: Omit<FunnelStep, "id">;
  secondStage?: Omit<FunnelStep, "id">;
  didPerform?: boolean;
}): EventCondition => {
  const eventCondition: EventCondition = {
    ...initialEventCondition,

    eventModelId: stage.eventModelId ?? null,
    relationshipId: stage.relationshipId ?? null,

    subconditions: stage.subconditions,
  };

  if (secondStage) {
    eventCondition.funnelCondition = {
      ...initialFunnelCondition,

      eventModelId: secondStage.eventModelId ?? null,
      relationshipId: secondStage.relationshipId ?? null,

      didPerform,

      subconditions: secondStage.subconditions,
    };
  }

  return eventCondition;
};

export const getTimeValueAndSelectedDates = (
  lookbackWindow:
    | {
        start: string;
        end: string;
      }
    | {
        lookback: TimeOptions | number;
      }
    | undefined,
): { timeValue: TimeOptions; selectedDates: string[] } => {
  let timeValue: TimeOptions = TimeOptions.Custom;
  const selectedDates: string[] = [];

  if (lookbackWindow && "lookback" in lookbackWindow) {
    if (typeof lookbackWindow.lookback === "number") {
      switch (lookbackWindow.lookback) {
        case SECONDS_IN_ONE_DAY * 30:
          timeValue = TimeOptions.ThirtyDays;
          break;
        case SECONDS_IN_ONE_DAY * 60:
          timeValue = TimeOptions.SixtyDays;
          break;
        case SECONDS_IN_ONE_DAY * 90:
          timeValue = TimeOptions.NinetyDays;
          break;
        case SECONDS_IN_ONE_DAY * 7:
        default:
          timeValue = TimeOptions.SevenDays;
          break;
      }
    } else {
      timeValue = lookbackWindow.lookback;
    }
  }

  if (
    timeValue === TimeOptions.Custom &&
    lookbackWindow &&
    "start" in lookbackWindow
  ) {
    selectedDates.push(lookbackWindow.start, lookbackWindow.end);
  }

  return { timeValue, selectedDates: selectedDates.filter(isPresent) };
};

export const isGroupByColumnRelatedToParent = (
  parent: ParentModel | null,
  groupByColumn: GroupByColumn | undefined,
) => {
  if (!groupByColumn) {
    return false;
  }

  const { buckets, ...column } = groupByColumn;

  // Traits are always related to the parent model
  const isTrait =
    (isRelatedColumn(column) || isTransformedColumn(column)) &&
    isTraitColumn(column.column);
  if (isTrait) return true;

  return Boolean(
    parent?.filterable_audience_columns?.find(({ column_reference }) =>
      // in case modelId is not a string
      isEqualWith(column_reference, column, numberAndStringValidator),
    ),
  );
};

// Supports both events and related models
export const isGroupByColumnRelatedToModel = (
  relationship: Relationship | null,
  groupByColumn: GroupByColumn | undefined,
) => {
  if (!groupByColumn) {
    return false;
  }

  const { buckets, ...column } = groupByColumn;
  return Boolean(
    relationship?.to_model?.filterable_audience_columns?.find(
      ({ column_reference }) =>
        // in case modelId is not a string
        isEqualWith(column_reference, column, numberAndStringValidator),
    ),
  );
};

export const getColumnReferenceFromGroupByColumn = (
  column: GroupByColumn | GroupColumn,
): ColumnReference => {
  return "column" in column ? column.column : column;
};

export const findAudienceFromCohort = (
  audiences: SelectedAudience[],
  cohortDefinition: AnalyticsCohortDefinitionOutput | null | undefined,
  cohortId: number,
) => {
  const cohortFilter = cohortDefinition?.filter;
  // Find the audience by the filter if it's an ad hoc audience since these
  // audiences can have the same id as the parent model
  return audiences.find((a) => {
    if (cohortFilter?.conditions?.length) {
      // We only want to compare name to name or filter to filter if we know the
      // audience is an ad hoc audience so we avoid comparing empty conditions
      if (!isAdHocAudience(a)) {
        return false;
      }

      return (
        a.name === cohortDefinition?.name || isEqual(a.filter, cohortFilter)
      );
    }

    return a.id == cohortId && !isAdHocAudience(a);
  });
};

export const shouldUsePercentFormat = (
  aggregation: AggregationOption | undefined,
  normalization: NormalizationType | undefined,
) => {
  return (
    aggregation === AggregationOption.PercentOfAudience ||
    normalization === NormalizationType.LiftPercent ||
    // Normalization rates should always show as a percentage for better UX
    normalization === NormalizationType.RatePerUser ||
    normalization === NormalizationType.RatePerInteraction
  );
};

const isChartTab = (chart_type: string): chart_type is ChartTab => {
  return Object.values(ChartTab).includes(chart_type as ChartTab);
};

export const transformSavedChartTab = (chart_type: string): ChartTab => {
  if (isChartTab(chart_type)) return chart_type;

  return ChartTab.Insights;
};

export const transformSavedChartGraphType = (
  chart_type: string,
): GraphType | undefined => {
  if (chart_type === "funnel") {
    return undefined;
  }

  // Handle legacy chart types as well (default to line graph for any other legacy chart types)
  switch (chart_type) {
    case "table":
      return GraphType.Table;
    case "heatmap":
      return GraphType.HeatMap;
    case "breakdown":
    case "bar":
      return GraphType.Bar;
    case "column":
      return GraphType.Column;
    case "area":
      return GraphType.Area;
    case "big-number":
      return GraphType.BigNumber;
    case "line":
    default:
      return GraphType.Line;
  }
};

export const isValidMeasuringSelection = (
  measuringSelection: MeasuringSelection | undefined,
) => {
  if (measuringSelection?.scope === MeasurementScope.DecisionEngineFlow) {
    return measuringSelection?.id !== null;
  }

  // We're currently only referencing the measuring selection for AID flows so
  // assume it's fine otherwise
  return true;
};
